import type { HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '../lib/cn';

export interface TreeNode {
  id: string;
  label: string;
  icon?: ReactNode;
  /**
   * Optional rich visual content for the row (h3-3: thumbnail + GTIN + status badges per
   * product row). When present it replaces the plain-text label visually; `label` remains the
   * row's accessible name (rendered as `aria-label`) so screen readers still get a concise
   * summary regardless of what `content` renders.
   */
  content?: ReactNode;
  /** Per-column cell content, keyed by `TreeTableColumn.id`; only used when the table has `columns`. */
  cells?: Record<string, ReactNode>;
  children?: TreeNode[];
  defaultExpanded?: boolean;
  disabled?: boolean;
}

export interface TreeTableColumn {
  id: string;
  /** Plain header text; packages/ui has no i18n, so the consumer passes the translated label. */
  label: string;
  /** Optional slot that replaces `label` in the header (e.g. a sortable header button). */
  header?: ReactNode;
  ariaSort?: 'ascending' | 'descending' | 'none' | 'other';
  /**
   * Width classes applied to both the header and every cell of this column. Must fix the width
   * (e.g. `w-[110px] shrink-0`); otherwise header and cells size to their own content and drift apart.
   */
  className: string;
}

export interface TreeTableProps extends Omit<HTMLAttributes<HTMLDivElement>, 'aria-label' | 'children' | 'role'> {
  ariaLabel: string;
  /** When given, a header row is rendered inside the treegrid and each row gets one gridcell per column. */
  columns?: TreeTableColumn[];
  nodes: TreeNode[];
  expandedIds?: readonly string[];
  selectedId?: string | null;
  activeId?: string;
  onActiveIdChange?: (id: string) => void;
  onExpandedIdsChange?: (ids: string[]) => void;
  onOpen?: (node: TreeNode) => void;
  onSelectedIdChange?: (id: string | null) => void;
  role?: string;
}

interface VisibleNode {
  node: TreeNode;
  level: number;
  parentId?: string;
  position: number;
  setSize: number;
}

function collectDefaultExpanded(nodes: TreeNode[], expanded = new Set<string>()) {
  nodes.forEach((node) => {
    if (node.defaultExpanded && !node.disabled) expanded.add(node.id);
    if (node.children) collectDefaultExpanded(node.children, expanded);
  });
  return expanded;
}

function collectExpandableIds(nodes: TreeNode[], ids = new Set<string>()) {
  nodes.forEach((node) => {
    if (node.children?.length && !node.disabled) ids.add(node.id);
    if (node.children) collectExpandableIds(node.children, ids);
  });
  return ids;
}

function collectNodeIds(nodes: TreeNode[], ids = new Set<string>()) {
  nodes.forEach((node) => {
    ids.add(node.id);
    if (node.children) collectNodeIds(node.children, ids);
  });
  return ids;
}

function getVisibleNodes(nodes: TreeNode[], expanded: Set<string>, level = 1, parentId?: string): VisibleNode[] {
  return nodes.flatMap((node, index) => [
    { node, level, parentId, position: index + 1, setSize: nodes.length },
    ...(node.children && expanded.has(node.id) ? getVisibleNodes(node.children, expanded, level + 1, node.id) : []),
  ]);
}

export function TreeTable({ activeId: controlledActiveId, ariaLabel, className, columns: columnsProp, expandedIds, nodes, onActiveIdChange, onExpandedIdsChange, onOpen, onSelectedIdChange, selectedId: controlledSelectedId, ...props }: TreeTableProps) {
  const instanceId = useId();
  // An empty array behaves exactly like no columns.
  const columns = columnsProp?.length ? columnsProp : undefined;
  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(() => collectDefaultExpanded(nodes));
  const [uncontrolledActiveId, setUncontrolledActiveId] = useState(nodes.find((node) => !node.disabled)?.id ?? '');
  const [uncontrolledSelectedId, setUncontrolledSelectedId] = useState<string | null>(null);
  const expanded = useMemo(() => expandedIds ? new Set(expandedIds) : uncontrolledExpanded, [expandedIds, uncontrolledExpanded]);
  const visibleNodes = useMemo(() => getVisibleNodes(nodes, expanded), [nodes, expanded]);
  const availableNodeIds = useMemo(() => collectNodeIds(nodes), [nodes]);
  const effectiveActiveId = (controlledActiveId ?? uncontrolledActiveId) && visibleNodes.some((item) => item.node.id === (controlledActiveId ?? uncontrolledActiveId) && !item.node.disabled)
    ? controlledActiveId ?? uncontrolledActiveId
    : visibleNodes.find((item) => !item.node.disabled)?.node.id ?? '';
  const selectedId = controlledSelectedId ?? uncontrolledSelectedId;
  // Header spacer covers the chevron slot (size-5 + gap-2 = w-7), plus the icon slot (size-4 + gap-2) when rows show icons.
  const headerSpacer = visibleNodes.some((item) => item.node.icon) ? 'w-13' : 'w-7';

  useEffect(() => {
    const expandableIds = collectExpandableIds(nodes);
    if (expandedIds === undefined) {
      setUncontrolledExpanded((current) => {
        const next = new Set([...current].filter((id) => expandableIds.has(id)));
        collectDefaultExpanded(nodes).forEach((id) => next.add(id));
        return next;
      });
    }
    if (!availableNodeIds.has(uncontrolledSelectedId ?? '')) setUncontrolledSelectedId(null);
    if (!availableNodeIds.has(uncontrolledActiveId)) setUncontrolledActiveId(nodes.find((node) => !node.disabled)?.id ?? '');
  }, [availableNodeIds, expandedIds, nodes, uncontrolledActiveId, uncontrolledSelectedId]);

  const setActive = (id: string) => {
    setUncontrolledActiveId(id);
    onActiveIdChange?.(id);
  };
  const setSelected = (id: string | null) => {
    setUncontrolledSelectedId(id);
    onSelectedIdChange?.(id);
  };
  const toggle = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (expandedIds === undefined) setUncontrolledExpanded(next);
    onExpandedIdsChange?.([...next]);
  };
  const moveFocus = (id: string) => {
    rowRefs.current.get(id)?.focus();
  };
  const adjacentFocusable = (index: number, direction: 1 | -1) => {
    for (let nextIndex = index + direction; nextIndex >= 0 && nextIndex < visibleNodes.length; nextIndex += direction) {
      const next = visibleNodes[nextIndex];
      if (next && !next.node.disabled) return next;
    }
    return undefined;
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, item: VisibleNode, index: number) => {
    const { node, parentId } = item;
    if (node.disabled) { event.preventDefault(); return; }
    const hasChildren = Boolean(node.children?.length);
    if (event.key === 'ArrowDown') { event.preventDefault(); const next = adjacentFocusable(index, 1); if (next) moveFocus(next.node.id); }
    if (event.key === 'ArrowUp') { event.preventDefault(); const previous = adjacentFocusable(index, -1); if (previous) moveFocus(previous.node.id); }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (hasChildren && !expanded.has(node.id)) toggle(node.id);
      else if (hasChildren) {
        const child = visibleNodes.slice(index + 1).find((candidate) => candidate.level > item.level && !candidate.node.disabled);
        if (child) moveFocus(child.node.id);
      }
    }
    if (event.key === 'ArrowLeft') { event.preventDefault(); if (hasChildren && expanded.has(node.id)) toggle(node.id); else if (parentId) moveFocus(parentId); }
    if (event.key === 'Enter') { event.preventDefault(); onOpen?.(node); }
  };

  return (
    <div {...props} aria-colcount={columns?.length} aria-label={ariaLabel} className={cn('overflow-x-auto', className)} role="treegrid">
      {columns ? (
        <div className="flex w-full min-w-max items-center gap-x-2 border-b border-[var(--border)] bg-[var(--background-muted)] py-2 pr-3 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--foreground-muted)]" role="row">
          {columns.map((column, index) => (
            <span aria-colindex={index + 1} aria-sort={column.ariaSort} className={cn('flex min-w-0 items-center', column.className)} key={column.id} role="columnheader">
              {index === 0 ? <span aria-hidden="true" className={cn('inline-block shrink-0', headerSpacer)} /> : null}
              {column.header ?? column.label}
            </span>
          ))}
        </div>
      ) : null}
      {visibleNodes.map((item, index) => {
        const { node, level } = item;
        const hasChildren = Boolean(node.children?.length);
        return <div
          aria-disabled={node.disabled || undefined}
          aria-expanded={hasChildren ? expanded.has(node.id) : undefined}
          aria-label={node.label}
          aria-level={level}
          aria-posinset={item.position}
          aria-selected={selectedId === node.id}
          aria-setsize={item.setSize}
          className={cn('flex w-full min-w-max cursor-default items-center border-b border-[var(--border)] py-2.5 pr-3 text-sm text-[var(--foreground)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)] focus-visible:ring-inset', selectedId === node.id && 'bg-[var(--primary-light)]', node.disabled && 'cursor-not-allowed opacity-60', columns && 'gap-x-2')}
          id={`${instanceId}-tree-row-${node.id}`}
          key={node.id}
          onClick={() => { if (!node.disabled) setSelected(node.id); }}
          onFocus={() => setActive(node.id)}
          onKeyDown={(event) => onKeyDown(event, item, index)}
          ref={(element) => { if (element) rowRefs.current.set(node.id, element); else rowRefs.current.delete(node.id); }}
          role="row"
          tabIndex={!node.disabled && effectiveActiveId === node.id ? 0 : -1}
        >
          <span aria-colindex={columns ? 1 : undefined} className={cn('flex min-w-0 items-center', columns?.[0]?.className)} role="gridcell">
            <span aria-hidden="true" className="flex" data-tree-indent-wrapper>{Array.from({ length: Math.max(0, level - 1) }, (_, index) => <span className="inline-block pl-5" data-tree-indent key={index} />)}</span>
            <span className="flex min-w-0 items-center gap-2" data-tree-content>
              {hasChildren ? <button aria-label={node.label} className="flex size-5 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]" disabled={node.disabled} onClick={(event) => { event.stopPropagation(); if (!node.disabled) toggle(node.id); }} tabIndex={-1} type="button"><span aria-hidden="true" className={cn('transition-transform', expanded.has(node.id) && 'rotate-90')}>›</span></button> : <span aria-hidden="true" className="inline-block size-5" />}
              {node.icon ? <span aria-hidden="true" className="flex size-4 items-center justify-center">{node.icon}</span> : null}
              {(columns?.[0] && node.cells?.[columns[0].id]) ?? node.content ?? <span>{node.label}</span>}
            </span>
          </span>
          {columns?.slice(1).map((column, index) => <span aria-colindex={index + 2} className={cn('flex min-w-0 items-center', column.className)} key={column.id} role="gridcell">{node.cells?.[column.id]}</span>)}
        </div>;
      })}
    </div>
  );
}
